import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Paper,
  useTheme,
  alpha,
  Skeleton,
  Avatar,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import DashboardsMenu from '../../components/DashboardsMenu/DashboardsMenu';
import Filters from '../../components/Filters/Filters';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { VocuherCategory } from '../../utils/common-utils';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { verifyInviteToken } from '../../store/userThunk';
import { useSearchParams } from 'react-router-dom';
import {
  fetchARDashboardNumbers,
  fetchARDashboardSales,
  fetchARDashboardAging,
  fetchARDashboardCustomersDue,
  fetchARDashboardTagInvoices,
  fetchARDashboardPaymentMode
} from '../../store/ARDashboardStore/ARDashboardThunk';
import {
  fetchAPDashboardNumbers,
  fetchAPDashboardSales,
  fetchAPDashboardExpenses,
  fetchAPDashboardCustomersDue
} from '../../store/APDashboardStore/APDashboardThunk';
import { ApiStatus } from '../../utils/api.types';
import { Utils } from '../../utils/common-utils';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import DonutLargeRoundedIcon from '@mui/icons-material/DonutLargeRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import Chart from 'react-apexcharts';
import Button from '@mui/material/Button';
import BusinessSummarySend from '../../components/BusinessSummary/BusinessSummarySend';

const ACCENT = '#84cc16';
const ACCENT_LIGHT = '#d9f99d';
const ACCENT_SOFT = '#ecfccb';
const BG = '#fafafb';
const SURFACE = '#ffffff';
const TEXT = '#0f172a';
const MUTED = '#64748b';
const BORDER = '#cbd5e1';

// Distinct slice colours for the tag donut — no two neighbours look alike.
const TAG_PALETTE = ['#84cc16', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#14b8a6'];

const scaleToggleSx = {
  height: 26,
  backgroundColor: '#f1f5f9',
  borderRadius: 999,
  p: '2px',
  boxSizing: 'border-box',
  '& .MuiToggleButtonGroup-grouped': {
    border: 0,
    borderRadius: '999px !important',
    px: 1.25,
    py: 0,
    color: '#64748b',
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'none',
    '&.Mui-selected': {
      backgroundColor: '#ffffff',
      color: '#0f172a',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.15)',
      '&:hover': { backgroundColor: '#ffffff' }
    }
  }
};

const cardSx = {
  borderRadius: 4,
  border: `1px solid ${BORDER}`,
  boxShadow: 'none',
  backgroundColor: SURFACE
};

// Compact currency — Cr for >=1 Cr, else L (Lakh)
const fmtShort = (num: number): string => {
  if (num == null || isNaN(num)) return '₹ 0';
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (abs >= 10000000) return `${sign}₹ ` + (abs / 10000000).toFixed(2).replace(/\.?0+$/, '') + ' Cr';
  return `${sign}₹ ` + (abs / 100000).toFixed(2).replace(/\.?0+$/, '') + ' L';
};

// Full rupee figure, never abbreviated — dashboard tables and totals show the
// exact amount, no K / L / Cr shortening.
const fmtFull = (num: number): string => Utils.formatFull(num);

const Home = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { user, selectedAccount } = useAppSelector(state => state.auth);
  const { tags: invoiceTags } = useAppSelector(state => state.tags);
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [filterParams, setFilterParams] = useState<string>('');
  // Refresh button bumps this; Filters resets to This FY Year and the new params refetch the data.
  const [resetDateKey, setResetDateKey] = useState(0);
  // Sales vs Purchase chart x-axis grouping; '' = coarsest scale the current range allows.
  const [trendScale, setTrendScale] = useState<'' | 'day' | 'week' | 'month'>('');
  // Tag widget: false = bar chart, true = table breakup (same data, two views).
  const [showTagTable, setShowTagTable] = useState(false);
  // Payment By Mode shows the same data as a bar chart or a receipt-wise table.
  const [showPaymentTable, setShowPaymentTable] = useState(false);
  // Payment By Mode table is paginated client-side; the API returns every receipt in the range.
  const [paymentPage, setPaymentPage] = useState(0);
  const [tagPage, setTagPage] = useState(0);
  const ROWS_PER_PAGE = 10;
  // Payment By Mode fits nine receipts in its card without an inner scrollbar.
  const PAYMENT_ROWS_PER_PAGE = 9;

  const handleFilterChange = (params: string) => {
    setFilterParams(params);
  };

  const ARDashboard = useAppSelector(state => state.ARDashboard);
  const APDashboard = useAppSelector(state => state.APDashboard);

  useEffect(() => {
    const token = searchParams.get('inviteToken');
    if (token) {
      dispatch(verifyInviteToken({ token }));
    }
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (selectedAccount?._id && filterParams) {
      fetchDashboardData(filterParams);
    }
  }, [selectedAccount, filterParams]);

  const fetchDashboardData = async (filters: string) => {
    if (!selectedAccount?._id) return;
    setIsLoading(true);

    try {
      const arPromises = [
        dispatch(fetchARDashboardNumbers({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardSales({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardAging({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardCustomersDue({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardTagInvoices({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardPaymentMode({ accountId: selectedAccount._id, params: filters }))
      ];

      const apPromises = [
        dispatch(fetchAPDashboardNumbers({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchAPDashboardSales({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchAPDashboardExpenses({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchAPDashboardCustomersDue({ accountId: selectedAccount._id, params: filters }))
      ];

      await Promise.allSettled([...arPromises, ...apPromises]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Listing-style footer: grand total on the left, "x-y of N items" + page
  // numbers on the right — same shape as the report listing screens.
  const TableFooterBar = ({
    grandTotal,
    page,
    onPageChange,
    totalRows,
    rowsPerPage = ROWS_PER_PAGE
  }: {
    grandTotal: string;
    page: number;
    onPageChange: (page: number) => void;
    totalRows: number;
    rowsPerPage?: number;
  }) => {
    if (totalRows === 0) return null;

    const pageCount = Math.ceil(totalRows / rowsPerPage);
    const from = page * rowsPerPage + 1;
    const to = Math.min(totalRows, (page + 1) * rowsPerPage);

    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        sx={{ mt: 'auto', pt: 1, borderTop: `1px solid ${BORDER}`, rowGap: 0.5 }}
      >
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: TEXT }}>
          Grand Total: {grandTotal}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: '0.68rem', color: MUTED }}>
            {from}-{to} of {totalRows} items
          </Typography>
          <Pagination
            size="small"
            shape="rounded"
            count={pageCount}
            page={page + 1}
            siblingCount={0}
            boundaryCount={1}
            onChange={(event, value) => onPageChange(value - 1)}
            sx={{
              '& .MuiPaginationItem-root': { fontSize: '0.68rem', minWidth: 24, height: 24, color: MUTED },
              '& .Mui-selected': { backgroundColor: 'transparent !important', border: `1px solid ${ACCENT}`, color: TEXT }
            }}
          />
        </Stack>
      </Stack>
    );
  };

  const MetricCard = ({
    title,
    value,
    trend,
    icon,
    accent = ACCENT,
    highlight = false
  }: {
    title: string;
    value: string | number;
    trend?: { hasGrown: boolean; value: number };
    icon: React.ReactNode;
    accent?: string;
    highlight?: boolean;
  }) => (
    <Card
      sx={{
        ...cardSx,
        backgroundColor: highlight ? ACCENT_SOFT : SURFACE,
        borderColor: highlight ? ACCENT_LIGHT : BORDER,
        height: '100%',
        minHeight: 90,
        transition: 'transform .2s ease, box-shadow .2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px -12px rgba(15,23,42,0.12)'
        }
      }}
    >
      <CardContent sx={{
        p: 1.75,
        '&:last-child': { pb: 1.75 },
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box flex={1} minWidth={0}>
            <Typography
              sx={{ color: MUTED, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.4px', lineHeight: 1.3, mb: 0.75 }}
              noWrap
            >
              {title}
            </Typography>
            <Typography
              sx={{ fontWeight: 700, color: TEXT, lineHeight: 1.2, fontSize: '0.98rem' }}
              noWrap
            >
              {isLoading ? <Skeleton width={70} /> : value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: highlight ? '#ffffff' : alpha(accent, 0.12),
              color: accent,
              flexShrink: 0
            }}
          >
            {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 17, color: accent } })}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  // Days in the loaded range: the backend fills one key per day for every series it returns.
  const rangeDays = useMemo(() => Math.max(
    Object.keys(ARDashboard.sales?.dashboardSales?.series || {}).length,
    Object.keys(APDashboard.sales?.dashboardSales?.series || {}).length,
    Object.keys(APDashboard.expenses?.dashboardExpenses?.series || {}).length
  ), [ARDashboard.sales?.dashboardSales?.series, APDashboard.sales?.dashboardSales?.series, APDashboard.expenses?.dashboardExpenses?.series]);

  // Scales the range can be shown at, coarsest first. Keeps the chart at ~31 points or fewer.
  const availableScales = useMemo<Array<'day' | 'week' | 'month'>>(() => {
    if (!rangeDays) return ['day'];
    const scales: Array<'day' | 'week' | 'month'> = [];
    if (rangeDays > 31) scales.push('month');
    if (rangeDays > 7 && rangeDays / 7 <= 27) scales.push('week');
    if (rangeDays <= 31) scales.push('day');
    return scales;
  }, [rangeDays]);
  const activeScale = (trendScale && availableScales.includes(trendScale)) ? trendScale : availableScales[0];

  // A new date range starts again from its coarsest scale once its data is in.
  useEffect(() => { setTrendScale(''); }, [rangeDays]);

  // Merge sales, purchase and expense series onto the same date labels so they align on the x-axis.
  const buildAlignedSeries = (
    arSeries: Record<string, any> | null | undefined,
    apSeries: Record<string, any> | null | undefined,
    expenseSeries: Record<string, any> | null | undefined,
    arCounts: Record<string, any> | null | undefined,
    apCounts: Record<string, any> | null | undefined,
    scale: 'day' | 'week' | 'month'
  ) => {
    // Backend sends keys like "2026-6-16" (no leading zeros) — normalize to "2026-06-16"
    const normalizeKey = (k: string) => {
      const parts = k.split('-');
      if (parts.length !== 3) return k;
      return `${parts[0]}-${parts[1].padStart(2,'0')}-${parts[2].padStart(2,'0')}`;
    };
    const toMap = (src: Record<string, any> | null | undefined) =>
      Object.fromEntries(Object.entries(src || {}).map(([k, v]) => [normalizeKey(k), Number(v) || 0]));

    const arMap = toMap(arSeries);
    const apMap = toMap(apSeries);
    const expenseMap = toMap(expenseSeries);
    const arCountMap = toMap(arCounts);
    const apCountMap = toMap(apCounts);

    const allDates = Array.from(new Set([...Object.keys(arMap), ...Object.keys(apMap), ...Object.keys(expenseMap)])).sort();
    const empty = { labels: [] as string[], groups: [] as { title: string; cols: number }[], months: [] as string[], arValues: [] as number[], apValues: [] as number[], expenseValues: [] as number[], arCounts: [] as number[], apCounts: [] as number[] };
    if (allDates.length === 0) return empty;

    const toDate = (k: string) => { const p = k.split('-'); return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2])); };
    const short = (d: Date) => d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    const monthOf = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    // Bucket key + label for a day at the selected scale. Day and week labels carry only day numbers;
    // the month is shown once as a group title under the axis.
    const bucketOf = (k: string) => {
      const d = toDate(k);
      if (scale === 'month') {
        return { key: `${d.getFullYear()}-${d.getMonth()}`, label: monthOf(d), group: '' };
      }
      if (scale === 'week') {
        // 7-day chunks counted from the first day of the range, so a month reads 1–7, 8–14, … with no stubs.
        const first = toDate(allDates[0]), last = toDate(allDates[allDates.length - 1]);
        const idx = Math.floor((d.getTime() - first.getTime()) / 86400000 / 7);
        const from = new Date(first); from.setDate(first.getDate() + idx * 7);
        const to = new Date(from); to.setDate(from.getDate() + 6);
        const end = to > last ? last : to;
        const label = from.getTime() === end.getTime() ? String(from.getDate())
          : from.getMonth() === end.getMonth() ? `${from.getDate()}–${end.getDate()}`
          : `${short(from)} – ${short(end)}`;
        return { key: `w${idx}`, label, group: monthOf(from) };
      }
      return { key: k, label: String(d.getDate()), group: monthOf(d) };
    };

    const buckets: Record<string, { label: string; group: string; ar: number; ap: number; exp: number; arN: number; apN: number }> = {};
    const order: string[] = [];
    allDates.forEach(k => {
      const { key, label, group } = bucketOf(k);
      if (!buckets[key]) { buckets[key] = { label, group, ar: 0, ap: 0, exp: 0, arN: 0, apN: 0 }; order.push(key); }
      buckets[key].ar += arMap[k] || 0;
      buckets[key].ap += apMap[k] || 0;
      buckets[key].exp += expenseMap[k] || 0;
      buckets[key].arN += arCountMap[k] || 0;
      buckets[key].apN += apCountMap[k] || 0;
    });

    // Consecutive buckets of the same month collapse into one axis group title.
    const groups: { title: string; cols: number }[] = [];
    order.forEach(k => {
      const g = buckets[k].group;
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.title === g) lastGroup.cols += 1; else groups.push({ title: g, cols: 1 });
    });

    return {
      labels: order.map(k => buckets[k].label),
      // Month titles only help when the range spans more than one month.
      groups: (scale === 'month' || groups.length < 2) ? [] : groups,
      months: order.map(k => buckets[k].group),
      arValues: order.map(k => buckets[k].ar),
      apValues: order.map(k => buckets[k].ap),
      expenseValues: order.map(k => buckets[k].exp),
      arCounts: order.map(k => buckets[k].arN),
      apCounts: order.map(k => buckets[k].apN),
    };
  };

  const {
    labels: trendCategories,
    groups: trendGroups,
    months: trendMonths,
    arValues: arTrendValues,
    apValues: apTrendValues,
    expenseValues: expenseTrendValues,
    arCounts: arTrendCounts,
    apCounts: apTrendCounts
  } = buildAlignedSeries(
    ARDashboard.sales?.dashboardSales?.series,
    APDashboard.sales?.dashboardSales?.series,
    APDashboard.expenses?.dashboardExpenses?.series,
    ARDashboard.sales?.dashboardSales?.counts,
    APDashboard.sales?.dashboardSales?.counts,
    activeScale
  );

  // Daily view of a month is too dense for the 7/12 column, so the chart takes the whole row.
  const trendWide = trendCategories.length > 12;
  const trendHeight = trendWide ? 300 : 220;

  const salesInvoiceCount = Number(ARDashboard.sales?.dashboardSales?.invoiceCount) || 0;
  const purchaseInvoiceCount = Number(APDashboard.sales?.dashboardSales?.invoiceCount) || 0;

  const salesChartOptions = {
    chart: {
      type: 'line' as const,
      height: 340,
      toolbar: { show: false },
      fontFamily: 'inherit',
      stacked: false,
      animations: { enabled: true, easing: 'easeinout' as const, speed: 600 }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: trendCategories.length <= 6 ? '22%' : trendCategories.length <= 12 ? '38%' : '55%',
        borderRadius: 6,
        borderRadiusApplication: 'end' as const
      }
    },
    dataLabels: { enabled: false },
    // Expense line is dashed so it reads apart from Purchase without relying on colour.
    stroke: { show: true, width: [0, 3, 3], curve: 'monotoneCubic' as const, dashArray: [0, 0, 6] },
    xaxis: {
      categories: trendCategories,
      labels: { rotate: 0, hideOverlappingLabels: false, trim: false, style: { colors: MUTED, fontSize: '11px' } },
      // Month name once under its days / weeks.
      group: { style: { fontSize: '11px', fontWeight: 600, colors: TEXT }, groups: trendGroups },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      min: 0,
      labels: {
        style: { colors: MUTED, fontSize: '11px' },
        formatter: (value: number) => value < 0 ? '' : Utils.formatCompact(value, { space: false })
      }
    },
    colors: ['#059669', '#f59e0b', '#2563eb'],
    fill: {
      opacity: [0.95, 1, 1],
      type: ['solid', 'solid', 'solid']
    },
    markers: {
      size: [0, 7, 6],
      colors: ['#059669', '#f59e0b', '#2563eb'],
      shape: ['circle', 'circle', 'square'] as any,
      strokeColors: '#fff',
      strokeWidth: 2,
      hover: { size: 9 }
    },
    tooltip: {
      shared: true,
      intersect: false,
      // Header: the bucket label plus how many sales / purchase invoices were created in it.
      x: {
        formatter: (value: any, opts: any) => {
          const i = opts?.dataPointIndex ?? -1;
          const label = trendCategories[i] ?? value;
          const month = trendMonths[i] || '';
          const when = month ? `${label} ${month}` : label;
          return `${when} · Sales - ${arTrendCounts[i] ?? 0} , Purchase - ${apTrendCounts[i] ?? 0}`;
        }
      },
      y: { formatter: (value: number) => Utils.formatNumber(value, true, false) }
    },
    grid: {
      borderColor: BORDER,
      strokeDashArray: 4,
      padding: { left: 8, right: 8 },
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } }
    },
    // Legend is drawn in the card header next to the scale toggle.
    legend: { show: false }
  };

  const salesChartSeries = [
    { name: 'Sales Revenue', type: 'column', data: arTrendValues },
    { name: 'Purchase Amount', type: 'line', data: apTrendValues },
    { name: 'Expenses', type: 'line', data: expenseTrendValues }
  ];

  const barBase = {
    chart: { type: 'bar' as const, height: 340, toolbar: { show: false }, fontFamily: 'inherit' },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '45%',
        borderRadius: 8,
        borderRadiusApplication: 'end' as const,
        distributed: true,
        dataLabels: { position: 'top' as const }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => Utils.formatCompact(val),
      offsetY: -22,
      style: { fontSize: '11px', fontWeight: 700, colors: [TEXT] }
    },
    yaxis: {
      min: 0,
      tickAmount: 4,
      labels: {
        style: { colors: MUTED, fontSize: '11px' },
        formatter: (value: number) => value < 0 ? '' : Utils.formatCompact(value)
      },
      forceNiceScale: true,
      max: (max: number) => Math.ceil(max * 1.25 / 100) * 100
    },
    legend: { show: false },
    tooltip: { y: { formatter: (value: number) => Utils.formatNumber(value, true, false) } },
    grid: { borderColor: BORDER, strokeDashArray: 4, xaxis: { lines: { show: false } }, padding: { top: 24 } }
  };

  // Tag-wise revenue — how much was invoiced under each tag in the range.
  const tagSeriesData = ARDashboard.tagInvoices?.dashboardTagInvoices?.series || [];
  const tagRows = ARDashboard.tagInvoices?.dashboardTagInvoices?.rows || [];
  // Sales via Tags only makes sense once the account has invoice tags (the dashboard Filters loads them).
  const hasInvoiceTags = invoiceTags.length > 0 || tagSeriesData.length > 0;

  const tagChartOptions = useMemo(() => ({
    ...barBase,
    chart: { ...barBase.chart, height: 300 },
    // Tag master colours are often near-identical, so the bars always use
    // this distinct palette instead.
    colors: tagSeriesData.map((tag: any, index: number) => TAG_PALETTE[index % TAG_PALETTE.length]),
    xaxis: {
      categories: tagSeriesData.map((tag: any) => tag.name),
      labels: { style: { colors: MUTED, fontSize: '11px' }, trim: true, rotateAlways: false, hideOverlappingLabels: false },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    tooltip: {
      y: {
        // One series, so the tag is picked by point index — seriesIndex is
        // always 0 here, unlike the donut this replaced.
        formatter: (value: number, opts: any) => {
          const tag = tagSeriesData[opts?.dataPointIndex];
          return `${fmtFull(value)} · ${tag?.total || 0} invoice(s)`;
        }
      }
    }
  }), [tagSeriesData]);

  // One bar per tag — sized by amount, not count.
  const tagChartSeries = useMemo(
    () => [{ name: 'Amount', data: tagSeriesData.map((tag: any) => tag.totalValue) }],
    [tagSeriesData]
  );

  // Payment mode breakup — how much was received through each mode.
  const paymentModeData = ARDashboard.paymentMode?.dashboardPaymentMode?.series || [];
  const paymentModeRows = ARDashboard.paymentMode?.dashboardPaymentMode?.rows || [];

  const paymentModeChartOptions = useMemo(() => ({
    chart: { type: 'bar' as const, toolbar: { show: false }, fontFamily: 'inherit', offsetY: 6 },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '45%',
        borderRadius: 6,
        borderRadiusApplication: 'end' as const,
        distributed: true,
        // Above the bar, not inside it — the value was landing on the coloured
        // fill in a dark colour and reading as invisible.
        dataLabels: { position: 'top' as const }
      }
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: '11px', fontWeight: 700, colors: [TEXT] },
      background: { enabled: false },
      offsetY: -20,
      formatter: (value: number) => fmtFull(value)
    },
    legend: { show: false },
    colors: TAG_PALETTE,
    xaxis: {
      categories: paymentModeData.map((mode: any) => mode.name),
      labels: { style: { colors: MUTED, fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      min: 0,
      labels: { style: { colors: MUTED, fontSize: '11px' }, formatter: (value: number) => Utils.formatCompact(value, { space: false }) }
    },
    grid: { borderColor: BORDER, strokeDashArray: 4, xaxis: { lines: { show: false } } },
    tooltip: {
      y: {
        formatter: (value: number, opts: any) => {
          const mode = paymentModeData[opts?.dataPointIndex];
          return `${fmtFull(value)} · ${mode?.total || 0} receipt(s)`;
        }
      }
    }
  }), [paymentModeData]);

  const paymentModeChartSeries = useMemo(() => [{
    name: 'Received',
    data: paymentModeData.map((mode: any) => mode.totalValue)
  }], [paymentModeData]);

  // A new date range means a shorter list — jump back to the first page.
  useEffect(() => {
    setPaymentPage(0);
    setTagPage(0);
  }, [filterParams]);


  const paymentModeVisibleRows = useMemo(
    () => paymentModeRows.slice(paymentPage * PAYMENT_ROWS_PER_PAGE, paymentPage * PAYMENT_ROWS_PER_PAGE + PAYMENT_ROWS_PER_PAGE),
    [paymentModeRows, paymentPage]
  );

  const tagVisibleRows = useMemo(
    () => tagRows.slice(tagPage * ROWS_PER_PAGE, tagPage * ROWS_PER_PAGE + ROWS_PER_PAGE),
    [tagRows, tagPage]
  );

  // Aging Chart - simple pie with side total
  const agingChartOptions = {
    chart: { type: 'donut' as const, height: 220, fontFamily: 'inherit' },
    labels: ARDashboard.aging?.dashboardAging?.series ?
      Object.keys(ARDashboard.aging.dashboardAging.series) : [],
    colors: [ACCENT, '#f59e0b', '#ef4444', '#8b5cf6', '#3b82f6'],
    dataLabels: {
      enabled: true,
      style: { fontSize: '11px', fontWeight: 700, colors: ['#fff'] },
      formatter: (val: number) => `${val.toFixed(0)}%`,
      dropShadow: { enabled: false }
    },
    stroke: { width: 2, colors: ['#fff'] },
    legend: {
      position: 'bottom' as const,
      horizontalAlign: 'center' as const,
      fontSize: '11px',
      markers: { width: 8, height: 8, radius: 12 } as any,
      labels: { colors: MUTED },
      itemMargin: { horizontal: 6, vertical: 2 }
    },
    plotOptions: {
      pie: {
        donut: { size: '60%', labels: { show: false } }
      }
    },
    tooltip: {
      y: { formatter: (val: number) => Utils.formatNumber(val, true, false) }
    }
  };

  const agingChartSeries = ARDashboard.aging?.dashboardAging?.series ?
    Object.values(ARDashboard.aging.dashboardAging.series).map(val => Number(val) || 0) : [];

  // Sales vs Received - use numbers totalAR + toCollect (balance) for accurate totals
  const arNums: any = ARDashboard.numbers?.dashboardNumbers || {};
  const totalSales = Number(arNums.totalAR || 0);
  const outstandingAR = Number(arNums.toCollect || arNums.arUnpaid || 0);
  const arFullyPaidAmt = Number(arNums.arFullyPaid || 0);
  const arPartiallyPaidReceivedAmt = Number(arNums.arPartiallyPaidReceived || 0);
  const amountReceived = Number(arNums.arPaid || (arFullyPaidAmt + arPartiallyPaidReceivedAmt) || 0);


  const salesReceivedChartOptions = useMemo(() => ({
    ...barBase,
    xaxis: {
      categories: ['Total Sales', 'Received', 'Outstanding'],
      labels: { style: { colors: MUTED, fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    colors: ['#059669', '#22c55e', '#ef4444'],
    tooltip: {
      y: { formatter: (value: number) => Utils.formatNumber(value, true, false) },
      custom: ({ dataPointIndex }: any) => {
        if (dataPointIndex === 1) {
          return `<div style="padding:8px 12px;font-family:inherit;background:#fff;min-width:200px;">
            <b style="color:#0f172a;font-size:13px;">Received</b><br/>
            <span style="color:#22c55e;font-weight:700;font-size:14px;">${Utils.formatNumber(amountReceived, true, false)}</span>
            <div style="margin-top:6px;font-size:11px;color:#64748b;">
              Fully Paid: <b style="color:#22c55e;">${Utils.formatNumber(arFullyPaidAmt, true, false)}</b><br/>
              Partially Paid (Received): <b style="color:#3b82f6;">${Utils.formatNumber(arPartiallyPaidReceivedAmt, true, false)}</b>
            </div>
          </div>`;
        }
        const labels = ['Total Sales', 'Received', 'Outstanding'];
        const values = [totalSales, amountReceived, outstandingAR];
        const colors = ['#059669', '#22c55e', '#ef4444'];
        return `<div style="padding:8px 12px;font-family:inherit;background:#fff;"><b style="color:#0f172a;font-size:13px;">${labels[dataPointIndex]}</b><br/><span style="color:${colors[dataPointIndex]};font-weight:700;font-size:14px;">${Utils.formatNumber(values[dataPointIndex], true, false)}</span></div>`;
      }
    }
  }), [totalSales, amountReceived, outstandingAR, arFullyPaidAmt, arPartiallyPaidReceivedAmt]);

  const salesReceivedChartSeries = useMemo(() => [{
    name: 'Amount',
    data: [totalSales, amountReceived, outstandingAR]
  }], [totalSales, amountReceived, outstandingAR]);

  const apNums: any = APDashboard.numbers?.dashboardNumbers || {};
  const totalPurchase = Number(apNums.totalAP || 0);
  const outstandingAP = Number(apNums.toPay || apNums.apUnpaid || 0);
  const apFullyPaidAmt = Number(apNums.apFullyPaid || 0);
  const apPartiallyPaidReceivedAmt = Number(apNums.apPartiallyPaidReceived || 0);
  const amountPaid = Number(apNums.apPaid || (apFullyPaidAmt + apPartiallyPaidReceivedAmt) || 0);

  const purchasePaidChartOptions = useMemo(() => ({
    ...barBase,
    xaxis: {
      categories: ['Total Purchase', 'Paid', 'Outstanding'],
      labels: { style: { colors: MUTED, fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    colors: ['#059669', '#22c55e', '#ef4444'],
    tooltip: {
      y: { formatter: (value: number) => Utils.formatNumber(value, true, false) },
      custom: ({ dataPointIndex }: any) => {
        if (dataPointIndex === 1) {
          return `<div style="padding:8px 12px;font-family:inherit;background:#fff;min-width:200px;">
            <b style="color:#0f172a;font-size:13px;">Paid</b><br/>
            <span style="color:#22c55e;font-weight:700;font-size:14px;">${Utils.formatNumber(amountPaid, true, false)}</span>
            <div style="margin-top:6px;font-size:11px;color:#64748b;">
              Fully Paid: <b style="color:#22c55e;">${Utils.formatNumber(apFullyPaidAmt, true, false)}</b><br/>
              Partially Paid (Paid): <b style="color:#3b82f6;">${Utils.formatNumber(apPartiallyPaidReceivedAmt, true, false)}</b>
            </div>
          </div>`;
        }
        const labels = ['Total Purchase', 'Paid', 'Outstanding'];
        const values = [totalPurchase, amountPaid, outstandingAP];
        const colors = ['#059669', '#22c55e', '#ef4444'];
        return `<div style="padding:8px 12px;font-family:inherit;background:#fff;"><b style="color:#0f172a;font-size:13px;">${labels[dataPointIndex]}</b><br/><span style="color:${colors[dataPointIndex]};font-weight:700;font-size:14px;">${Utils.formatNumber(values[dataPointIndex], true, false)}</span></div>`;
      }
    }
  }), [totalPurchase, amountPaid, outstandingAP, apFullyPaidAmt, apPartiallyPaidReceivedAmt]);

  const purchasePaidChartSeries = useMemo(() => [{
    name: 'Amount',
    data: [totalPurchase, amountPaid, outstandingAP]
  }], [totalPurchase, amountPaid, outstandingAP]);

  const initials = (name?: string) =>
    (name || 'NA').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const avatarColors = ['#84cc16', '#f59e0b', '#3b82f6', '#8b5cf6', '#ef4444'];

  const PerformerRow = ({ item, index, color }: { item: any; index: number; color: string }) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        py: 0.65,
        px: 0.5,
        borderRadius: 2,
        transition: 'background-color .2s',
        '&:hover': { backgroundColor: alpha(color, 0.06) }
      }}
    >
      <Avatar
        sx={{
          width: 30, height: 30,
          backgroundColor: alpha(color, 0.15),
          color,
          fontWeight: 700,
          fontSize: '0.7rem'
        }}
      >
        {initials(item.customer?.name)}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Typography sx={{ fontWeight: 600, color: TEXT, fontSize: '0.75rem' }} noWrap>
          {item.customer?.name || 'N/A'}
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.65rem' }}>
          {fmtFull(item.totalAmount || 0)}
        </Typography>
      </Box>
      <Box textAlign="right">
        <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.75rem' }}>
          {fmtFull(item.totalOutstanding || 0)}
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.6rem' }}>
          Outstanding
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 2 }, backgroundColor: BG, minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Header Row */}
      <Stack
        className="dash-header"
        // Filter line sits beside the greeting from lg (the green buttons live on their own
        // line below it, so it fits); on tablets and phones the greeting takes its own line.
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        {/* Never shrinks, so the greeting is never cut off; the action row wraps instead. */}
        <Box sx={{ flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: { xs: '1.1rem', md: '1.25rem' } }} noWrap>
            Hi {user?.name || 'there'} 👋
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: '0.75rem' }} noWrap>
            {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })} · {selectedAccount?.name || 'your account'}
          </Typography>
        </Box>
        <Stack
          direction="row"
          // Top-aligned so the Custom Date pickers opening below don't shift the rest of the row.
          alignItems="flex-start"
          sx={{
            width: { xs: '100%', lg: 'auto' },
            maxWidth: '100%',
            // gap instead of spacing so wrapped lines don't pick up a stray left margin.
            gap: { xs: 1, lg: 0.75 },
            // Wide screens keep everything on one line; as it narrows the counts and the two
            // green buttons drop to the next line with their labels intact, in the same order.
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            flexShrink: 1,
            minWidth: 0
          }}
        >
          <IconButton
            onClick={() => setResetDateKey(key => key + 1)}
            disabled={isLoading}
            sx={{
              width: 38,
              height: 38,
              backgroundColor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: '50%',
              flexShrink: 0,
              '&:hover': { backgroundColor: '#eff6ff', borderColor: '#bfdbfe' }
            }}
          >
            <RefreshIcon sx={{ fontSize: 22, color: '#2563eb' }} />
          </IconButton>
          {/* display:contents on this box and on the Filters root lifts Date, Location and the
              Custom Date pickers into this row's flex flow, so the pickers can be ordered onto
              the second line next to the green buttons instead of pushing the count pills. */}
          <Box sx={{
            display: 'contents',
            // !important: the Filters root sets display:flex inline.
            '& > div': { display: 'contents !important' },
            // 200px so the longest option, "This FY Year (Apr - Mar)", is not cut off.
            // flex-grow off: Filters gives these controls flex 1 so they'd stretch across this row.
            '& > div > .MuiInputBase-root, & > div > .MuiAutocomplete-root': { flex: '0 0 auto !important', maxWidth: { xs: '200px !important', lg: '150px !important' } },
            '& > div > .MuiAutocomplete-root': { width: { xs: 200, lg: 150 } },
            '& .MuiTextField-root': { minWidth: '120px !important', maxWidth: { xs: '200px !important', lg: '150px !important' } },
            // c-text-field-3's -3px margin made neighbouring boxes touch.
            '& .c-text-field-3': { marginLeft: '0 !important' },
            '& .filters-custom-dates': { order: 2, flexShrink: 0 }
          }}>
            <Filters
              type={VocuherCategory.SALE}
              inline
              filterHandler={handleFilterChange}
              hideFilter={true}
              hidePartySearch={true}
              hideReset={true}
              showLocation={true}
              noPersist={true}
              resetDateKey={resetDateKey}
            />
          </Box>
          <Tooltip title="Buyers">
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              height: 38,
              px: { xs: 1.5, lg: 1 },
              borderRadius: 999,
              backgroundColor: SURFACE,
              border: `1px solid ${BORDER}`,
              boxSizing: 'border-box',
              flexShrink: 0
            }}>
              <PeopleIcon sx={{ fontSize: 14, color: '#8b5cf6' }} />
              {/* Label hides below xl to keep the header on one row; the tooltip still names it. */}
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: MUTED, lineHeight: 1 }}>Buyers</Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: TEXT, lineHeight: 1 }}>
                {ARDashboard.numbers?.dashboardNumbers?.totalCustomers || 0}
              </Typography>
            </Box>
          </Tooltip>
          <Tooltip title="Suppliers">
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              height: 38,
              px: { xs: 1.5, lg: 1 },
              borderRadius: 999,
              backgroundColor: SURFACE,
              border: `1px solid ${BORDER}`,
              boxSizing: 'border-box',
              flexShrink: 0
            }}>
              <LocalShippingIcon sx={{ fontSize: 14, color: '#ec4899' }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: MUTED, lineHeight: 1 }}>Suppliers</Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: TEXT, lineHeight: 1 }}>
                {ARDashboard.numbers?.dashboardNumbers?.totalVendors || 0}
              </Typography>
            </Box>
          </Tooltip>
          {/* The two counts stay on one row of their own at every width, on the filter line. */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: 0.75,
            flexShrink: 0,
            maxWidth: '100%'
          }}>
          <Tooltip title="Total sales invoices created in this range">
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              height: 38,
              px: { xs: 1.5, lg: 1 },
              borderRadius: 999,
              backgroundColor: SURFACE,
              border: `1px solid ${BORDER}`,
              boxSizing: 'border-box',
              flexShrink: 0
            }}>
              <ReceiptLongIcon sx={{ fontSize: 14, color: '#059669' }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: MUTED, lineHeight: 1 }}>Sales<Box component="span" sx={{ display: { lg: 'none' } }}> Created</Box></Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: TEXT, lineHeight: 1 }}>
                {salesInvoiceCount}
              </Typography>
            </Box>
          </Tooltip>
          <Tooltip title="Total purchase invoices created in this range">
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              height: 38,
              px: { xs: 1.5, lg: 1 },
              borderRadius: 999,
              backgroundColor: SURFACE,
              border: `1px solid ${BORDER}`,
              boxSizing: 'border-box',
              flexShrink: 0
            }}>
              <ShoppingCartIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: MUTED, lineHeight: 1 }}>Purchases<Box component="span" sx={{ display: { lg: 'none' } }}> Created</Box></Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: TEXT, lineHeight: 1 }}>
                {purchaseInvoiceCount}
              </Typography>
            </Box>
          </Tooltip>
          </Box>
          {/* Line break: everything with order >= 1 (Custom Date pickers, green buttons) sits on the second line. */}
          <Box sx={{ flexBasis: '100%', height: 0, order: 1 }} />
          {/* The two green buttons are one unit, so Business Dashboard never splits off from
              Business Summary onto the next line. */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            justifyContent: 'flex-end',
            gap: { xs: 1, lg: 0.75 },
            flexShrink: 0,
            order: 2
          }}>
          {selectedAccount?._id && (
            <BusinessSummarySend
              accountId={selectedAccount._id}
              renderTrigger={(onOpen) => (
                <Tooltip title="Business Summary">
                <Button
                  onClick={onOpen}
                  startIcon={<AssessmentOutlinedIcon sx={{ color: '#fff !important' }} />}
                  sx={{
                    backgroundColor: '#059669',
                    color: '#fff',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', md: '0.82rem', lg: '0.78rem' },
                    borderRadius: 999,
                    border: '1px solid #059669',
                    boxShadow: 'none',
                    px: { xs: 1.25, md: 1.75, lg: 1.25 },
                    // Same 38px box as the count pills beside it, so the row lines up.
                    height: 38,
                    py: 0,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    minWidth: 'auto',
                    '& .MuiButton-startIcon': { mr: 0.75 },
                    '&:hover': { backgroundColor: '#047857', borderColor: '#047857', color: '#fff' }
                  }}
                >
                  <Box component="span">Business Summary</Box>
                </Button>
                </Tooltip>
              )}
            />
          )}
          <DashboardsMenu />
          </Box>
        </Stack>
      </Stack>

      {/* Combined Metrics Row - 6 cards */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Total Sales"
            value={Utils.formatNumber(totalSales, true, false)}
            trend={ARDashboard.sales?.dashboardSales?.trend}
            icon={<TrendingUpIcon />}
            accent={ACCENT}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Total Received"
            value={Utils.formatNumber(amountReceived, true, false)}
            icon={<AccountBalanceWalletIcon />}
            accent="#22c55e"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Total Unpaid (AR)"
            value={Utils.formatNumber(outstandingAR, true, false)}
            icon={<AccessTimeIcon />}
            accent="#ef4444"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Total Purchases"
            value={Utils.formatNumber(totalPurchase, true, false)}
            trend={APDashboard.sales?.dashboardSales?.trend}
            icon={<ShoppingCartIcon />}
            accent="#f59e0b"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Total Paid"
            value={Utils.formatNumber(amountPaid, true, false)}
            icon={<PaymentIcon />}
            accent="#3b82f6"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Total Unpaid (AP)"
            value={Utils.formatNumber(outstandingAP, true, false)}
            icon={<AccessTimeIcon />}
            accent="#dc2626"
          />
        </Grid>
      </Grid>


      {/* Rows 1–3: trend chart, aging, sales/purchase bars, tags, payment mode — one container so cards reflow */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={12} md={trendWide ? 12 : 7}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>
                Sales vs Purchase vs Expense
              </Typography>
              <Stack direction="row" alignItems="center" gap={1.5} sx={{ ml: 'auto', flexShrink: 0 }}>
                {[['Sales Revenue', '#059669'], ['Purchase Amount', '#f59e0b'], ['Expenses', '#2563eb']].map(([name, color]) => (
                  <Stack key={name} direction="row" alignItems="center" gap={0.5}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '3px', backgroundColor: color }} />
                    <Typography sx={{ fontSize: '0.72rem', color: MUTED, whiteSpace: 'nowrap' }}>{name}</Typography>
                  </Stack>
                ))}
                {availableScales.length > 1 && (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={activeScale}
                    onChange={(event, value) => { if (value) setTrendScale(value); }}
                    sx={scaleToggleSx}
                  >
                    {availableScales.map(scale => (
                      <ToggleButton key={scale} value={scale} aria-label={`${scale} scale`}>
                        {scale === 'day' ? 'Daily' : scale === 'week' ? 'Weekly' : 'Monthly'}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}
              </Stack>
            </Stack>
            {isLoading && ARDashboard.sales?.status !== ApiStatus.SUCCESS ? (
              <Skeleton variant="rectangular" height={trendHeight} sx={{ borderRadius: 2 }} />
            ) : trendCategories.length === 0 ? (
              <Box sx={{ height: trendHeight, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: MUTED }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: MUTED }}>No transactions found</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: MUTED, mt: 0.5 }}>Try selecting a wider date range</Typography>
              </Box>
            ) : (
              <Chart key={`${activeScale}-${trendWide}`} options={salesChartOptions as any} series={salesChartSeries as any} type="bar" height={trendHeight} />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={trendWide ? 6 : 12} md={trendWide ? 4 : 5}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>
                Aging Analysis
              </Typography>
              <Box textAlign="right">
                <Typography sx={{ fontSize: '0.6rem', color: MUTED, lineHeight: 1 }}>Total</Typography>
                <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.78rem', lineHeight: 1.3 }}>
                  {fmtShort(ARDashboard.aging?.dashboardAging?.agingValue || 0)}
                </Typography>
              </Box>
            </Stack>
            {isLoading && ARDashboard.aging?.status !== ApiStatus.SUCCESS ? (
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            ) : agingChartSeries.length === 0 ? (
              <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>
                No aging data
              </Box>
            ) : (
              <Chart options={agingChartOptions} series={agingChartSeries} type="donut" height={220} />
            )}
          </Paper>
        </Grid>

        {/* Sales/Purchase bars. Same container as the trend chart: when it goes full width,
            Aging + these two share the next row three across. */}
        <Grid item xs={12} sm={6} md={(trendWide || !hasInvoiceTags) ? 4 : 6}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem', mb: 0.5 }}>
              Sales vs Received
            </Typography>
            {isLoading && (ARDashboard.sales?.status !== ApiStatus.SUCCESS || ARDashboard.customerDue?.status !== ApiStatus.SUCCESS) ? (
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            ) : (
              <Chart options={salesReceivedChartOptions} series={salesReceivedChartSeries} type="bar" height={200} />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={(trendWide || !hasInvoiceTags) ? 4 : 6}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem', mb: 0.5 }}>
              Purchase vs Paid
            </Typography>
            {isLoading && (APDashboard.sales?.status !== ApiStatus.SUCCESS || APDashboard.customerDue?.status !== ApiStatus.SUCCESS) ? (
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            ) : (
              <Chart options={purchasePaidChartOptions} series={purchasePaidChartSeries} type="bar" height={200} />
            )}
          </Paper>
        </Grid>

        {/* Sales via Tags + Payment by mode. Without tags, Payment by mode joins the two bar cards on one row. */}
        {hasInvoiceTags && (
        <Grid item xs={12} md={6}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Roomier than the usual mb:1 — the subtitle that used to sit under
                the title is gone, so the table header needs its own breathing room. */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>
                Sales via Tags
              </Typography>
              {/* Chart/Table toggle — abhi ke liye hataya gaya hai.
              <ToggleButtonGroup
                exclusive
                size="small"
                value={showTagTable ? 'table' : 'chart'}
                // A pill with two segments reads better here than an on/off switch,
                // because neither view is the "off" state of the other.
                onChange={(event, value) => { if (value) setShowTagTable(value === 'table'); }}
                sx={{
                  backgroundColor: BG,
                  borderRadius: 999,
                  p: '2px',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 0,
                    borderRadius: '999px !important',
                    px: 1.25,
                    py: 0.35,
                    gap: 0.5,
                    color: MUTED,
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&.Mui-selected': {
                      backgroundColor: SURFACE,
                      color: TEXT,
                      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.12)',
                      '&:hover': { backgroundColor: SURFACE }
                    }
                  }
                }}
              >
                <ToggleButton value="chart" aria-label="Chart view">
                  <BarChartRoundedIcon sx={{ fontSize: 14 }} />
                  Chart
                </ToggleButton>
                <ToggleButton value="table" aria-label="Table view">
                  <TableRowsRoundedIcon sx={{ fontSize: 14 }} />
                  Table
                </ToggleButton>
              </ToggleButtonGroup>
              */}
            </Stack>

            {isLoading && ARDashboard.tagInvoices?.status !== ApiStatus.SUCCESS ? (
              <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
            ) : tagSeriesData.length === 0 ? (
              <Box sx={{ flex: 1, minHeight: 240, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: MUTED }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: MUTED }}>No tagged invoices</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: MUTED, mt: 0.5 }}>Tag invoices while creating them to see this breakup</Typography>
              </Box>
            ) : showTagTable ? (
              <TableContainer sx={{ flex: 1, maxHeight: 300, overflowX: 'auto' }}>
                {/* Same fixed layout as Payment By Mode next to it, so both cards
                    space their columns identically. */}
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%', '& .MuiTableCell-root': { px: 0.75, py: 0.6 }, '& .MuiTableCell-root:last-of-type': { pr: 2 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: MUTED, backgroundColor: BG, whiteSpace: 'nowrap' }}>Date</TableCell>
                      {tagSeriesData.map((tag: any) => (
                        <TableCell key={tag.tagId} align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', color: MUTED, backgroundColor: BG, whiteSpace: 'nowrap' }}>
                          <Tooltip title={`${tag.total} invoice(s) · ${fmtFull(tag.totalValue)}`} arrow>
                            <span>{tag.name}</span>
                          </Tooltip>
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', color: TEXT, backgroundColor: BG }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tagVisibleRows.map((row: any) => (
                      <TableRow key={row.date} hover>
                        <TableCell sx={{ fontSize: '0.72rem', color: TEXT, whiteSpace: 'nowrap' }}>
                          {Utils.formatDateLong(row.date)}
                        </TableCell>
                        {tagSeriesData.map((tag: any) => (
                          <TableCell key={tag.tagId} align="right" sx={{ fontSize: '0.72rem', color: MUTED }}>
                            <Tooltip
                              title={`${tag.name} · ${Utils.formatDateLong(row.date)} · ${row.tags?.[tag.tagId]?.total || 0} invoice(s) · ${fmtFull(row.tags?.[tag.tagId]?.totalValue || 0)}`}
                              arrow
                            >
                              <span>{fmtFull(row.tags?.[tag.tagId]?.totalValue || 0)}</span>
                            </Tooltip>
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontSize: '0.72rem', fontWeight: 700, color: TEXT }}>
                          <Tooltip title={`${row.total} invoice(s) · ${fmtFull(row.totalValue)}`} arrow>
                            <span>{fmtFull(row.totalValue)}</span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '100%' }}>
                  <Chart options={tagChartOptions as any} series={tagChartSeries as any} type="bar" height={300} />
                </Box>
              </Box>
            )}
            {showTagTable && (
              <TableFooterBar
                grandTotal={fmtFull(ARDashboard.tagInvoices?.dashboardTagInvoices?.totalValue || 0)}
                page={tagPage}
                onPageChange={setTagPage}
                totalRows={tagRows.length}
              />
            )}
          </Paper>
        </Grid>
        )}

        <Grid item xs={12} md={hasInvoiceTags ? 6 : (trendWide ? 12 : 4)}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>
                Payment By Mode
              </Typography>
              {/* Chart/Table toggle — abhi ke liye hataya gaya hai.
              <ToggleButtonGroup
                exclusive
                size="small"
                value={showPaymentTable ? 'table' : 'chart'}
                onChange={(event, value) => { if (value) setShowPaymentTable(value === 'table'); }}
                sx={{
                  backgroundColor: BG,
                  borderRadius: 999,
                  p: '2px',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 0,
                    borderRadius: '999px !important',
                    px: 1.25,
                    py: 0.35,
                    gap: 0.5,
                    color: MUTED,
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&.Mui-selected': {
                      backgroundColor: SURFACE,
                      color: TEXT,
                      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.12)',
                      '&:hover': { backgroundColor: SURFACE }
                    }
                  }
                }}
              >
                <ToggleButton value="chart" aria-label="Chart view">
                  <DonutLargeRoundedIcon sx={{ fontSize: 14 }} />
                  Chart
                </ToggleButton>
                <ToggleButton value="table" aria-label="Table view">
                  <TableRowsRoundedIcon sx={{ fontSize: 14 }} />
                  Table
                </ToggleButton>
              </ToggleButtonGroup>
              */}
            </Stack>

            {isLoading && ARDashboard.paymentMode?.status !== ApiStatus.SUCCESS ? (
              <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
            ) : paymentModeRows.length === 0 ? (
              <Box sx={{ flex: 1, minHeight: 240, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: MUTED }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: MUTED }}>No payments received</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: MUTED, mt: 0.5 }}>Try selecting a wider date range</Typography>
              </Box>
            ) : !showPaymentTable ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '100%' }}>
                  <Chart options={paymentModeChartOptions as any} series={paymentModeChartSeries as any} type="bar" height={300} />
                </Box>
              </Box>
            ) : (
              <TableContainer sx={{ flex: 1, maxHeight: 300, overflowX: 'auto' }}>
                {/* Fixed layout so all eight columns get the same width instead of
                    the amount columns hogging whatever the text needs. */}
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%', minWidth: 660, '& .MuiTableCell-root': { px: 0.75, py: 0.6 }, '& .MuiTableCell-root:last-of-type': { pr: 2 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: MUTED, backgroundColor: BG, whiteSpace: 'nowrap' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: MUTED, backgroundColor: BG, whiteSpace: 'nowrap' }}>
                        <Tooltip title="Time the receipt was created" arrow>
                          <span>Time</span>
                        </Tooltip>
                      </TableCell>
                      {paymentModeData.map((mode: any) => (
                        <TableCell key={mode.mode} align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', color: MUTED, backgroundColor: BG, whiteSpace: 'nowrap' }}>
                          <Tooltip title={`${mode.total} receipt(s) · ${fmtFull(mode.totalValue)}`} arrow>
                            <span>{mode.name}</span>
                          </Tooltip>
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', color: TEXT, backgroundColor: BG }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentModeVisibleRows.map((row: any, index: number) => (
                      <TableRow key={row.id || `${row.date}-${index}`} hover>
                        <TableCell sx={{ fontSize: '0.72rem', color: TEXT, whiteSpace: 'nowrap' }}>
                          {Utils.formatDateLong(row.date)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: MUTED, whiteSpace: 'nowrap' }}>
                          {Utils.formatTime(row.recordedAt || row.date)}
                        </TableCell>
                        {paymentModeData.map((mode: any) => (
                          <TableCell key={mode.mode} align="right" sx={{ fontSize: '0.72rem', color: MUTED, whiteSpace: 'nowrap' }}>
                            {row.modes?.[mode.mode] ? (
                              <Tooltip
                                title={`${mode.name} · ${Utils.formatDateLong(row.date)} ${Utils.formatTime(row.recordedAt || row.date)} · ${fmtFull(row.modes[mode.mode].totalValue || 0)}`}
                                arrow
                              >
                                <span>{fmtFull(row.modes[mode.mode].totalValue || 0)}</span>
                              </Tooltip>
                            ) : (
                              <span>—</span>
                            )}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontSize: '0.72rem', fontWeight: 700, color: TEXT, whiteSpace: 'nowrap' }}>
                          <Tooltip title={`${Utils.formatDateLong(row.date)} ${Utils.formatTime(row.recordedAt || row.date)} · ${fmtFull(row.totalValue)}`} arrow>
                            <span>{fmtFull(row.totalValue)}</span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {showPaymentTable && (
              <TableFooterBar
                grandTotal={fmtFull(ARDashboard.paymentMode?.dashboardPaymentMode?.totalValue || 0)}
                page={paymentPage}
                onPageChange={setPaymentPage}
                totalRows={paymentModeRows.length}
                rowsPerPage={PAYMENT_ROWS_PER_PAGE}
              />
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Row 4: Top Customers + Top Vendors side by side */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem', mb: 0.5 }}>
              Top Customers · Outstanding
            </Typography>
            <Box>
              {isLoading && ARDashboard.customerDue?.status !== ApiStatus.SUCCESS ? (
                [...Array(4)].map((_, i) => <Skeleton key={i} height={42} sx={{ mb: 0.25 }} />)
              ) : (ARDashboard.customerDue?.dashboardCustomerDue?.list?.length || 0) === 0 ? (
                <Box sx={{ minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>No data</Box>
              ) : (
                ARDashboard.customerDue?.dashboardCustomerDue?.list?.slice(0, 5).map((item: any, index: number) => (
                  <PerformerRow key={index} item={item} index={index} color={avatarColors[index % avatarColors.length]} />
                ))
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem', mb: 0.5 }}>
              Top Vendors · Outstanding
            </Typography>
            <Box>
              {isLoading && APDashboard.customerDue?.status !== ApiStatus.SUCCESS ? (
                [...Array(4)].map((_, i) => <Skeleton key={i} height={42} sx={{ mb: 0.25 }} />)
              ) : (APDashboard.customerDue?.dashboardCustomerDue?.list?.length || 0) === 0 ? (
                <Box sx={{ minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>No data</Box>
              ) : (
                APDashboard.customerDue?.dashboardCustomerDue?.list?.slice(0, 5).map((item: any, index: number) => (
                  <PerformerRow key={index} item={item} index={index} color={avatarColors[(index + 2) % avatarColors.length]} />
                ))
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Home;
